import { Group, Highlight, Stack } from "@mantine/core";
import { Spotlight, spotlight } from "@mantine/spotlight";
import type { Dispatch, SetStateAction } from "react";
import { generatePath, useNavigate } from "react-router";
import { usePersons } from "../../../hooks/usePersons";
import { ROUTES } from "../../../routes";
import { MENU_ITEMS } from "../../Menu/Menu.constants";
import type { TQuicksearchWeightedSite } from "../Quicksearch.types";
import { Quicklink } from "./Quicklink";
import { QuicklinkLoader } from "./QuicklinkLoader";

interface QuicksearchSiteProps {
  item: TQuicksearchWeightedSite;
  highlight?: string[];
  setSearchFavorites: Dispatch<SetStateAction<Array<TQuicksearchWeightedSite>>>;
}

function QuicksearchSite({
  item,
  setSearchFavorites,
  highlight = []
}: QuicksearchSiteProps) {
  const navigate = useNavigate();
  const { persons, isLoading } = usePersons({ siteId: item.id });
  const isErloesmonitoringActive = persons?.some(
    (person) => person.isErloesmonitoringActive
  );
  const isParagraph_6EegActive = persons?.some(
    (person) => person.isParagraph_6EegActive
  );
  const isVermarktungActive =
    isErloesmonitoringActive || isParagraph_6EegActive;
  const isMieterstromActive = persons?.some(
    (person) => person.isInMieterstromProject
  );

  const handleClick = () => {
    addToSearchFavorites();
    if (item.project_id) {
      navigate(
        generatePath(ROUTES.site.entry, {
          siteId: item.id.toString(),
          projectId: item.project_id.toString()
        })
      );
    } else {
      navigate(
        generatePath(ROUTES.site.siteWithoutProject, {
          siteId: item.id.toString()
        })
      );
    }

    spotlight.close();
  };

  function addToSearchFavorites() {
    if (item.project_id === undefined) {
      // if the item has no project_id, then remove it from favorites
      setSearchFavorites((prevFavorites) =>
        prevFavorites.filter((fav) => fav.id !== item.id)
      );
      return;
    }

    setSearchFavorites((prevFavorites) => {
      const now = Date.now();
      // Update or add the favorite
      const updatedFavorites = prevFavorites.map((fav) =>
        fav.id === item.id
          ? {
              ...fav,
              weight: (fav.weight || 1) + 1,
              dateLastUpdated: now,
              variant: item.variant,
              project_id: item.project_id
            }
          : fav
      );
      if (!prevFavorites.some((fav) => fav.id === item.id)) {
        updatedFavorites.push({
          id: item.id,
          name: item.name,
          variant: item.variant,
          project_id: item.project_id,
          weight: 1,
          dateLastUpdated: now
        });
      }

      // Find the newest favorite
      const [newest, ...rest] = updatedFavorites.sort(
        (a, b) => (b.dateLastUpdated ?? 0) - (a.dateLastUpdated ?? 0)
      );
      // Sort the rest by weight
      // rest.sort((a, b) => (b.weight || 0) - (a.weight || 0));
      // Compose the top 10: newest first, then by weight
      const topFavorites = [newest, ...rest].slice(0, 10);

      // Remove items not in top 10 and older than 30 days
      const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
      const nowTs = Date.now();
      const keepFavorites = new Set(topFavorites.map((fav) => fav.id));
      const filteredFavorites = [
        ...topFavorites,
        ...updatedFavorites.filter(
          (fav) =>
            !keepFavorites.has(fav.id) &&
            fav.dateLastUpdated &&
            nowTs - fav.dateLastUpdated <= THIRTY_DAYS
        )
      ];
      return filteredFavorites;
    });
  }

  return (
    <Group justify="space-between" key={item.name}>
      <Spotlight.Action flex={1} w="auto" onClick={handleClick}>
        <Stack gap={0}>
          <Highlight highlight={highlight}>{item.name}</Highlight>

          {item.id && (
            <Highlight highlight={highlight} opacity={0.6} size="xs">
              {String(item.id)}
            </Highlight>
          )}
        </Stack>
      </Spotlight.Action>
      <Group gap="xs">
        {isLoading && <QuicklinkLoader />}
        {item.variant && !isLoading && (
          <>
            {isVermarktungActive && (
              <Quicklink
                Icon={MENU_ITEMS.VERMARKTUNG.icon}
                label={`${MENU_ITEMS.VERMARKTUNG.label} für ${item.name}`}
                to={generatePath(
                  `${MENU_ITEMS.VERMARKTUNG_PROJECT_LEVEL.url}?variantId=${item.variant}`,
                  {
                    projectId: item.project_id
                  }
                )}
              />
            )}
          </>
        )}
        {item.project_id && !isLoading && (
          <>
            <Quicklink
              Icon={MENU_ITEMS.TODOS.icon}
              label={`${MENU_ITEMS.TODOS.label} für ${item.name}`}
              to={generatePath(MENU_ITEMS.TODOS.url, {
                projectId: item.project_id
              })}
            />
            <Quicklink
              Icon={MENU_ITEMS.ENERGY_DATA.icon}
              label={`${MENU_ITEMS.ENERGY_DATA.label} für ${item.name}`}
              to={generatePath(MENU_ITEMS.ENERGY_DATA.url, {
                projectId: item.project_id
              })}
            />
            {isParagraph_6EegActive && (
              <Quicklink
                Icon={MENU_ITEMS.PARAGRAPH6.icon}
                label={`${MENU_ITEMS.PARAGRAPH6.label} für ${item.name}`}
                to={generatePath(MENU_ITEMS.PARAGRAPH6.url, {
                  projectId: item.project_id
                })}
              />
            )}
            {isMieterstromActive && (
              <Quicklink
                Icon={MENU_ITEMS.DELIVERY_OVERVIEW.icon}
                label={`${MENU_ITEMS.DELIVERY_OVERVIEW.label} für ${item.name}`}
                to={generatePath(MENU_ITEMS.DELIVERY_OVERVIEW.url, {
                  projectId: item.project_id
                })}
              />
            )}
          </>
        )}
      </Group>
    </Group>
  );
}

export { QuicksearchSite };
